import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import type { SelectProps, SelectOption } from './Select.types';
import { cn } from '../../lib/utils';
import { labelVariants } from '../Input/Input';
import { useFloatingDropdown } from '../LookupField/hooks/useFloatingDropdown';

export const Select: React.FC<SelectProps> = ({
  options = [],
  value,
  defaultValue,
  placeholder = 'Seleccionar...',
  disabled = false,
  error = false,
  label,
  description,
  helperText,
  errorMessage,
  showLabel = true,
  showDescription = true,
  menuHeader,
  onChange,
  onBlur,
  className = '',
  id,
  name,
  required = false,
  selectSize = 'base',
  startIcon,
  readOnly = false,
  menuPosition = 'bottom',
}) => {
  const [selected, setSelected] = useState<SelectOption | undefined>(
    options.find(opt => opt.value === value || opt.value === defaultValue)
  );

  useEffect(() => {
    if (value !== undefined) {
      setSelected(options.find(opt => opt.value === value));
    }
  }, [value, options]);

  const handleChange = (option: SelectOption) => {
    if (readOnly) return;
    setSelected(option);
    if (onChange) {
      onChange(option.value);
    }
  };

  const containerClasses = `
    relative
    w-full
    ${className}
  `.trim();

  const sizeClasses = {
    xs: "h-7 px-2 py-1 text-xs",
    sm: "h-8 px-2 py-1 text-sm",
    base: "h-9 px-3 py-1.5 text-base",
    l: "h-10 px-3 py-2 text-lg",
    xl: "h-11 px-4 py-2 text-xl",
  };

  const iconSizeMap = {
    xs: "w-3 h-3",
    sm: "w-3.5 h-3.5",
    base: "w-4 h-4",
    l: "w-5 h-5",
    xl: "w-6 h-6",
  };

  const isDisabledState = disabled && !readOnly;

  // Floating UI positioning — same pattern as LookupField (useFloatingDropdown +
  // portal to document.body). Without this, `Listbox.Options` positions itself with
  // `absolute` relative to its own wrapper, so a Select embedded in a constrained
  // container (a table cell/column, a flex row with no overflow) doesn't overlay on
  // top of it — the container grows/reflows to make room for the dropdown instead.
  const [isOpen, setIsOpen] = useState(false);
  const { refs: floatingRefs, floatingStyles } = useFloatingDropdown({
    placement: menuPosition === 'top' ? 'top-start' : 'bottom-start',
    enabled: isOpen,
  });
  // `Listbox.Options` unmounts (calls this ref with `null`) when Headless UI closes the
  // dropdown, and mounts it again (real node) when it opens — using that as the signal
  // for `isOpen` avoids re-deriving open state via a second, possibly-desynced source.
  const setFloatingPanelRef = useCallback(
    (node: HTMLUListElement | null) => {
      floatingRefs.setFloating(node);
      setIsOpen(node !== null);
    },
    [floatingRefs]
  );

  const buttonClasses = `
    w-full flex items-center justify-between rounded-md border border-input transition-colors
    focus:outline-none focus:ring-1 focus:ring-primary-400 focus:border-primary-400
    ${readOnly ? 'bg-zinc-100 dark:bg-zinc-800/80 text-zinc-500 dark:text-zinc-400 focus:ring-0 focus:border-input focus:ring-offset-0 cursor-default' : 'bg-transparent'}
    ${isDisabledState ? 'opacity-50 cursor-not-allowed bg-zinc-100 dark:bg-zinc-800/80 text-zinc-500 dark:text-zinc-400' : ''}
    ${sizeClasses[selectSize]}
    ${startIcon ? 'pl-9' : ''}
    ${error ? 'border-destructive ring-1 ring-destructive focus:ring-destructive focus:border-destructive text-destructive' : ''}
  `.trim();

  return (
    <div className={cn("grid w-full items-center gap-2", className)}>
      {(showLabel && label) || (showDescription && description) ? (
        <div className="flex flex-col gap-2 w-full">
          {showLabel && label && (
            <div className="flex items-center justify-between w-full">
              <label
                htmlFor={id}
                className={cn("text-content-primary dark:text-dark-content-primary", labelVariants({ size: selectSize }))}
              >
                {label}
                {required && <span className="text-destructive ml-1">*</span>}
              </label>
            </div>
          )}
          {showDescription && description && (
            <p className={cn(selectSize === 'xs' ? 'text-xs' : 'text-sm', error ? 'text-destructive' : 'text-muted-foreground')}>
              {description}
            </p>
          )}
        </div>
      ) : null}

      <Listbox value={selected} onChange={handleChange} disabled={disabled || readOnly} name={name}>
        <div className="relative w-full">
          {startIcon && (
            <div className={`absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center justify-center z-10 ${error ? 'text-destructive' : 'text-muted-foreground'} ${iconSizeMap[selectSize]}`}>
              {React.cloneElement(startIcon as React.ReactElement<any>, {
                className: cn((startIcon as React.ReactElement<any>).props.className, iconSizeMap[selectSize])
              })}
            </div>
          )}
          <Listbox.Button
            ref={floatingRefs.setReference}
            className={buttonClasses}
            id={id}
            onBlur={onBlur as any}
          >
            <span className={`block truncate text-left w-full pr-6 ${!selected ? (error ? 'text-destructive/60' : 'text-muted-foreground') : ''} ${readOnly || disabled ? 'text-zinc-500 dark:text-zinc-400' : ''}`}>
              {selected ? selected.label : placeholder}
            </span>
            <span className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center z-10 ${error ? 'text-destructive' : 'text-muted-foreground'} ${readOnly || disabled ? 'text-zinc-500 dark:text-zinc-400' : ''} ${iconSizeMap[selectSize]}`}>
              <ChevronUpDownIcon
                className={iconSizeMap[selectSize]}
                aria-hidden="true"
              />
            </span>
          </Listbox.Button>
          {createPortal(
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options
                ref={setFloatingPanelRef}
                style={{ ...floatingStyles, zIndex: 9999 }}
                className="max-h-60 overflow-auto rounded-md border border-input bg-zinc-100 dark:bg-zinc-800 text-popover-foreground py-1 shadow-md ring-1 ring-black/5 focus:outline-none"
              >
                {menuHeader && (
                  <div className="px-3 py-1 text-xs font-medium text-muted-foreground tracking-wider">
                    {menuHeader}
                  </div>
                )}
                {options.length === 0 ? (
                  <div className="relative cursor-default select-none px-3 py-2 text-muted-foreground text-sm">
                    No hay opciones
                  </div>
                ) : (
                  options.map((option) => (
                    <Listbox.Option
                      key={option.value}
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-3 pr-9 text-sm outline-none transition-colors ${
                          active ? 'bg-primary-100 text-primary dark:bg-primary-950 dark:text-primary-300' : ''
                        } ${option.disabled ? 'opacity-50 cursor-not-allowed' : ''}`
                      }
                      value={option}
                      disabled={option.disabled}
                    >
                      {({ selected, active }) => (
                        <>
                          <span
                            className={`block truncate ${
                              selected ? 'font-medium' : 'font-normal'
                            }`}
                          >
                            {option.label}
                          </span>
                          {selected ? (
                            <span
                              className="absolute inset-y-0 right-0 flex items-center pr-3 text-primary dark:text-primary-300"
                            >
                              <CheckIcon className="h-4 w-4" aria-hidden="true" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Listbox.Option>
                  ))
                )}
              </Listbox.Options>
            </Transition>,
            document.body
          )}
        </div>
      </Listbox>
      {(helperText || errorMessage) && (
        <p className={cn(selectSize === 'xs' ? 'text-xs' : 'text-sm', error ? 'text-destructive' : 'text-muted-foreground')}>
          {error && errorMessage ? errorMessage : helperText}
        </p>
      )}
    </div>
  );
};
